import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus } from '@phosphor-icons/react'
import type { SortingState, PaginationState } from '@tanstack/react-table'
import { Button } from '@/components/ui/button'
import { OrgDataTable } from '@/components/organizations/OrgDataTable'
import { OrgFilters } from '@/components/organizations/OrgFilters'
import { orgApi } from '@/api/organizations'

export function OrganizationList() {
  const navigate = useNavigate()

  // Table state
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  })
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'created_at', desc: true },
  ])

  // Filter state
  const [search, setSearch] = useState('')
  const [verticalSlug, setVerticalSlug] = useState<string | undefined>()
  const [isActive, setIsActive] = useState<boolean | null>(null)
  const [subscriptionTier, setSubscriptionTier] = useState<string | undefined>()

  // Reset pagination when filters change
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value)
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }, [])

  const handleVerticalChange = useCallback((value: string | undefined) => {
    setVerticalSlug(value)
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }, [])

  const handleStatusChange = useCallback((value: boolean | null) => {
    setIsActive(value)
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }, [])

  const handleTierChange = useCallback((value: string | undefined) => {
    setSubscriptionTier(value)
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }, [])

  // Data fetching
  const { data, isLoading } = useQuery({
    queryKey: [
      'organizations',
      'list',
      pagination,
      sorting,
      search,
      verticalSlug,
      isActive,
      subscriptionTier,
    ],
    queryFn: () =>
      orgApi.list({
        page: pagination.pageIndex + 1,
        per_page: pagination.pageSize,
        sort_by: sorting[0]?.id,
        sort_order: sorting[0]?.desc ? 'desc' : 'asc',
        search: search || undefined,
        vertical_slug: verticalSlug,
        is_active: isActive ?? undefined,
        subscription_tier: subscriptionTier,
      }),
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div />
        <Button onClick={() => navigate('/organizations/new')} size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Create Organization
        </Button>
      </div>

      <OrgFilters
        search={search}
        onSearchChange={handleSearchChange}
        verticalSlug={verticalSlug}
        onVerticalChange={handleVerticalChange}
        isActive={isActive}
        onStatusChange={handleStatusChange}
        subscriptionTier={subscriptionTier}
        onTierChange={handleTierChange}
      />

      <OrgDataTable
        data={data?.items ?? []}
        total={data?.total ?? 0}
        page={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        isLoading={isLoading}
      />
    </div>
  )
}
